import { useState, useEffect } from "react";

export interface DomainContext {
	isMainPlatform: boolean;
	tenantSlug: string | null;
	domain: string;
}

/**
 * Hook to detect current domain context
 * Determines if request is from main platform or tenant domain
 */
export function useDomain(): DomainContext {
	const [context, setContext] = useState<DomainContext>({
		isMainPlatform: false,
		tenantSlug: null,
		domain: "",
	});

	useEffect(() => {
		const hostname = window.location.hostname;
		const domain = window.location.origin;

		// Check if this is localhost (development)
		if (hostname === "localhost" || hostname === "127.0.0.1") {
			// Check if there's a subdomain (e.g., tenant.localhost:3000)
			const parts = hostname.split(".");
			if (parts.length > 1 && parts[0] !== "localhost") {
				setContext({
					isMainPlatform: false,
					tenantSlug: parts[0],
					domain,
				});
			} else {
				// Main platform in development
				setContext({
					isMainPlatform: true,
					tenantSlug: null,
					domain,
				});
			}
			return;
		}

		// Check if this is the main platform domain
		// In production, this would be philolms.com
		const mainPlatformDomain = import.meta.env.VITE_MAIN_PLATFORM_DOMAIN || "philolms.com";
		const isMainPlatform = hostname === mainPlatformDomain || hostname === `www.${mainPlatformDomain}`;

		if (isMainPlatform) {
			setContext({
				isMainPlatform: true,
				tenantSlug: null,
				domain,
			});
		} else {
			// Extract tenant slug from subdomain
			// Format: tenant-slug.philolms.com
			const parts = hostname.split(".");
			const tenantDomainSuffix = import.meta.env.VITE_TENANT_DOMAIN_SUFFIX || "philolms.com";
			
			if (hostname.endsWith(`.${tenantDomainSuffix}`) && parts.length >= 3) {
				const tenantSlug = parts[0];
				setContext({
					isMainPlatform: false,
					tenantSlug,
					domain,
				});
			} else {
				// Custom domain - we'll need to fetch tenant info from API
				setContext({
					isMainPlatform: false,
					tenantSlug: null, // Will be resolved from API
					domain,
				});
			}
		}
	}, []);

	return context;
}

